// Generic right-click menu for a band (vehicle assignment or rental). Producer-only
// (the grid only opens it when canEdit). The page supplies the item list per kind.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface MenuItem {
  label: string;
  danger?: boolean;
  /** Non-clickable section label (renders as a divider + caption). */
  header?: boolean;
  onClick?: () => void;
}

interface Props {
  x: number;
  y: number;
  title: string;
  items: MenuItem[];
  onClose: () => void;
}

const BandContextMenu: React.FC<Props> = ({ x, y, title, items, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Keep the menu on-screen, by MEASURING it rather than guessing.
  //
  // This used to clamp against hardcoded 200×120. The rentals menu is the tallest
  // in the app (section headers plus a move-to-vehicle list), so 120 underestimated
  // it badly and right-clicking anywhere near the bottom of the window cut the
  // options off — exactly the report. Guessing a height cannot work for a menu
  // whose length is data-driven.
  //
  // First paint uses the raw cursor position with the menu hidden, then the
  // measured rect decides whether to flip above / left. One extra frame, and it is
  // correct for any menu length.
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<{ top: number; left: number } | null>(null);

  // useLayoutEffect, not useEffect: it runs before paint, so the measuring pass is
  // never visible. With useEffect the menu showed for one frame at the raw cursor
  // position before flipping.
  //
  // The measuring pass parks the menu at 0,0 rather than at the cursor. A
  // `position: fixed` element with no width is capped by the space to its right, so
  // measuring at `left: x` near the window edge returns a SQUEEZED width — then the
  // flip gives it more room, it lays out wider than measured, and a long vehicle
  // name clips anyway. At 0,0 it measures at its natural width.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const MARGIN = 8;
    // Flip above the cursor when it does not fit below, but never off the top:
    // a menu taller than the viewport pins to MARGIN and scrolls (max-h-[70vh]).
    const top = y + height + MARGIN > window.innerHeight
      ? Math.max(MARGIN, y - height)
      : y;
    const left = x + width + MARGIN > window.innerWidth
      ? Math.max(MARGIN, x - width)
      : x;
    setPlacement({ top, left });
  }, [x, y, items.length]);

  return (
    <>
      <div className="fixed inset-0 z-[1100]" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div
        ref={menuRef}
        className="fixed z-[1101] bg-gray-850 day:bg-white border border-gray-700 day:border-gray-200 rounded-md shadow-overlay day:shadow-xl day:shadow-gray-900/10 py-1 min-w-[180px] max-h-[70vh] overflow-y-auto"
        // Hidden for the measuring frame only — rendered (so it has a rect) but
        // not visible at the wrong spot, which would flash on every right-click.
        style={placement
          ? { top: placement.top, left: placement.left }
          // Measuring pass: 0,0 so the width is not clipped by the right edge.
          : { top: 0, left: 0, visibility: 'hidden' }}
      >
        <div className="px-3 py-1.5 text-[10px] text-on-surface-variant day:text-gray-500 uppercase tracking-wider truncate border-b border-gray-700/60 day:border-gray-200">
          {title}
        </div>
        {items.map((item, i) => (
          item.header ? (
            <div
              key={`h-${i}`}
              className="px-3 pt-2 pb-0.5 text-[10px] uppercase tracking-wider text-on-surface-variant day:text-gray-500 border-t border-gray-700/60 day:border-gray-200 mt-1"
            >
              {item.label}
            </div>
          ) : (
            <button
              key={`i-${i}`}
              onClick={item.onClick}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-container-highest day:hover:bg-gray-100 transition-colors ${item.danger ? 'text-red-400 day:text-red-600' : 'text-on-surface day:text-gray-800'}`}
            >
              {item.label}
            </button>
          )
        ))}
      </div>
    </>
  );
};

export default BandContextMenu;
